import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { BasicInfo } from '../types/kyc';
import { ipfsUriToUrl } from '../utils/ipfs';
import ImagePreview from './ImagePreview';

interface BasicInfoReviewFormProps {
  isWhitelisted:boolean;
  data: BasicInfo;
  status?: number; // KYC状态
  onReview?: (approved: boolean, comments?: string) => Promise<void>;
  submitting?: boolean;
  canReview?: boolean; // 是否有审核权限
  onReset?: () => Promise<void>; 
  canReset?: boolean; // 是否有重置权限
  reviewStatus?: 'pending' | 'approved' | 'rejected';
}

const BasicInfoReviewForm: React.FC<BasicInfoReviewFormProps> = ({
  isWhitelisted,
  data,
  status,
  onReview,
  submitting = false,
  canReview = false,
  onReset, 
  canReset = false,
  reviewStatus
}) => {
  const [comments, setComments] = React.useState<string>('');
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [bannerPreview, setBannerPreview] = React.useState<string>('');
  const handleReview = async (approved: boolean) => {
    if (onReview) {
      await onReview(approved, comments);
    }
  };
  const handleReset = async (approved: boolean) => {
    if (onReset) {
      await onReset();
    }
  };
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return 'Pending';
    }
  };
  useEffect(() => {
    if (data) { 
      // 如果有初始图片数据，设置预览
      if (data.image) {
        setImagePreview(ipfsUriToUrl(data.image));
      }
      if (data.banner) {
        setBannerPreview(ipfsUriToUrl(data.banner));
      }
    }
  }, [data]);
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Basic Information Review
          </Typography>
          {reviewStatus && (
            <Chip 
              label={getStatusText(reviewStatus)} 
              color={getStatusColor(reviewStatus) as any}
              size="small"
            />
          )}
        </Box>
        
        {submitting && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing...
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* 基本信息字段 - 只读 */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Name"
              value={data.name || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              value={data.email || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Website"
              value={data.website || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={data.description || ''}
              fullWidth
              multiline
              rows={3}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          {/* 社交媒体字段 - 只读 */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Social Media
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Twitter"
              value={data.social?.twitter || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Discord"
              value={data.social?.discord || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Telegram"
              value={data.social?.telegram || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          {/* 图片显示 - 只读 */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Images
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avatar Image
              </Typography>
              
              {data.image ? (
                <Box sx={{ display: 'inline-block' }}>
                  <ImagePreview
                    src={ipfsUriToUrl(data.image)}
                    alt="Avatar"
                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No avatar uploaded
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Banner Image
              </Typography>
              
              {data.banner ? (
                <Box sx={{ display: 'inline-block' }}>
                  <ImagePreview
                    src={ipfsUriToUrl(data.banner)}
                    alt="Banner"
                    style={{ width: 400, height: 100, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No banner uploaded
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* 审核功能 */}
          {canReview  && (
            <>
              {/* <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  审核意见
                </Typography>
                <TextField
                  label="审核备注（可选）"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="请输入审核意见..."
                />
              </Grid> */}
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                 <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleReset(true)}
                    disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    {submitting ? 'Processing...' : 'Reset'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleReview(false)}
                    disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleReview(true)}
                    disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    {submitting ? 'Processing...' : 'Approve'}
                  </Button>
                </Box>
              </Grid>
            </>
          )}
          
          {/* 只读状态提示 */}
          {!canReview && (
            <Grid item xs={12}>
              <Alert severity="info">
                You are viewing the basic information in read-only mode.
              </Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BasicInfoReviewForm;